import { RECREATE_AUDIO_DURATION, type RecreateAudio } from "./recreate"
import { fetchRemoteResourceFile } from "../media/resourceUpload"
import { validateLocalUploadFile } from "../media/uploadedResources"
import { uploadFile } from "~/api/upload"

/** Encode only the selected samples; the uploaded file cannot contain the tail. */
export const encodeAudioWav = (
  channels: Float32Array[],
  sampleRate: number,
  frameCount: number,
): ArrayBuffer => {
  const blockAlign = channels.length * 2
  const buffer = new ArrayBuffer(44 + frameCount * blockAlign)
  const view = new DataView(buffer)
  const writeText = (offset: number, value: string) => {
    for (let index = 0; index < value.length; index += 1) {
      view.setUint8(offset + index, value.charCodeAt(index))
    }
  }
  writeText(0, "RIFF")
  view.setUint32(4, buffer.byteLength - 8, true)
  writeText(8, "WAVE")
  writeText(12, "fmt ")
  view.setUint32(16, 16, true)
  view.setUint16(20, 1, true)
  view.setUint16(22, channels.length, true)
  view.setUint32(24, sampleRate, true)
  view.setUint32(28, sampleRate * blockAlign, true)
  view.setUint16(32, blockAlign, true)
  view.setUint16(34, 16, true)
  writeText(36, "data")
  view.setUint32(40, frameCount * blockAlign, true)
  for (let frame = 0; frame < frameCount; frame += 1) {
    channels.forEach((channel, index) => {
      const value = Math.max(-1, Math.min(1, channel[frame] ?? 0))
      view.setInt16(
        44 + frame * blockAlign + index * 2,
        value * (value < 0 ? 32768 : 32767),
        true,
      )
    })
  }
  return buffer
}

export const prepareReferenceAudio = async (
  file: File,
): Promise<{
  file: File
  duration: number
  trimmed: boolean
}> => {
  const validation = validateLocalUploadFile(file, "audio")
  if (!validation.valid) throw new Error(validation.message)
  const context = new AudioContext({ sampleRate: 24_000 })
  try {
    const audio = await context.decodeAudioData(await file.arrayBuffer())
    if (!Number.isFinite(audio.duration) || audio.duration <= 0) {
      throw new Error("无法读取有效的音频时长")
    }
    if (audio.duration <= RECREATE_AUDIO_DURATION) {
      return { file, duration: audio.duration, trimmed: false }
    }
    const frames = Math.floor(RECREATE_AUDIO_DURATION * audio.sampleRate)
    const channels = Array.from(
      { length: audio.numberOfChannels },
      (_, index) => audio.getChannelData(index),
    )
    const buffer = encodeAudioWav(channels, audio.sampleRate, frames)
    return {
      file: new File(
        [buffer],
        `${file.name.replace(/\.[^.]+$/, "")}-${RECREATE_AUDIO_DURATION}s.wav`,
        { type: "audio/wav" },
      ),
      duration: frames / audio.sampleRate,
      trimmed: true,
    }
  } finally {
    await context.close()
  }
}

export const prepareReferenceAudioResource = async (
  url: string,
  signal: AbortSignal,
): Promise<RecreateAudio> => {
  const file = await fetchRemoteResourceFile({
    fallbackName: "参考音频",
    kind: "audio",
    signal,
    url,
  })
  signal.throwIfAborted()
  const prepared = await prepareReferenceAudio(file)
  signal.throwIfAborted()
  const resource = prepared.trimmed
    ? (
        await uploadFile({
          file: prepared.file,
          type: "audio",
          signal,
        })
      ).data
    : {
        url,
        name: file.name,
        size: file.size,
        type: file.type,
        uploadedAt: Date.now(),
      }
  signal.throwIfAborted()
  if (!resource?.url) throw new Error("音频上传失败，请重试")
  return { ...resource, duration: prepared.duration, trimmed: prepared.trimmed }
}
